import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Car, Loader2, Plus, Trash2, Route } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({ date: today(), origin: "", destination: "", miles: "", round_trip: false, purpose: "" });

export const MileageTracker = ({ year, onChanged }) => {
  const [trips, setTrips] = useState([]);
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/vault/trips", { params: year ? { year } : {} }).then((r) => setTrips(r.data)).catch(() => {});
  useEffect(() => { load(); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/vault/mileage-rates").then((r) => setRates(r.data)).catch(() => {}); }, []);

  const rateFor = (d) => { let r = rates[rates.length - 1]?.rate || 0.76; rates.forEach((x) => { if (d >= x.from) r = x.rate; }); return r; };
  const previewMiles = (parseFloat(form.miles) || 0) * (form.round_trip ? 2 : 1);
  const previewDed = previewMiles * rateFor(form.date);

  const add = async () => {
    if (!form.origin.trim() || !form.destination.trim() || !form.miles) { toast.error("Add where you went and how many miles"); return; }
    setBusy(true);
    try {
      await api.post("/vault/trips", { ...form, miles: parseFloat(form.miles) });
      toast.success(`Trip logged · ${money(previewDed)} deduction`);
      setForm(empty()); setOpen(false); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not log trip"); } finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/vault/trips/${id}`); load(); onChanged?.(); } catch { toast.error("Could not delete"); }
  };

  const total = trips.reduce((s, t) => s + (t.deduction || 0), 0);
  const miles = trips.reduce((s, t) => s + (t.total_miles || 0), 0);

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="mileage-tracker">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#114A31]"><Car className="h-5 w-5" /><span className="font-heading font-semibold">Mileage</span></div>
          <p className="mt-1 text-sm text-[#595D5A]">{miles.toLocaleString(undefined, { maximumFractionDigits: 1 })} business miles · <span className="font-semibold text-[#1C1C1C]" data-testid="mileage-total">{money(total)}</span> deduction at the IRS rate</p>
        </div>
        <Button onClick={() => setOpen((o) => !o)} data-testid="mileage-add-toggle" className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Log a trip
        </Button>
      </div>

      {open && (
        <div className="mt-4 bg-[#F9F9F8] border border-[#E5E5E5] rounded-2xl p-4" data-testid="mileage-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="trip-date" className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Miles (one way)</Label><Input type="number" step="0.1" min="0" placeholder="12.5" value={form.miles} onChange={(e) => setForm({ ...form, miles: e.target.value })} data-testid="trip-miles" className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">From</Label><Input placeholder="Home office" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="trip-origin" className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">To</Label><Input placeholder="Client site, supplier, bank…" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="trip-destination" className="mt-1 rounded-xl" /></div>
            <div className="col-span-2"><Label className="text-xs">Purpose</Label><Input placeholder="Client meeting, pickup, site visit" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="trip-purpose" className="mt-1 rounded-xl" /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm text-[#1C1C1C] cursor-pointer">
              <Checkbox checked={form.round_trip} onCheckedChange={(v) => setForm({ ...form, round_trip: !!v })} data-testid="trip-roundtrip" className="border-[#114A31] data-[state=checked]:bg-[#114A31]" /> Round trip (doubles the miles)
            </label>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-[#595D5A]" data-testid="trip-preview">{previewMiles.toFixed(1)} mi × {(rateFor(form.date) * 100).toFixed(1)}¢ = <span className="font-semibold text-[#114A31]">{money(previewDed)}</span></p>
            <Button onClick={add} disabled={busy} data-testid="trip-save-btn" className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save trip"}
            </Button>
          </div>
        </div>
      )}

      {trips.length > 0 && (
        <ul className="mt-4 space-y-1.5" data-testid="trip-list">
          {trips.slice(0, 8).map((t) => (
            <li key={t.trip_id} className="flex items-center gap-2 text-sm bg-[#F9F9F8] rounded-xl px-3 py-2" data-testid={`trip-row-${t.trip_id}`}>
              <Route className="h-4 w-4 text-[#114A31] shrink-0" />
              <span className="text-xs text-[#595D5A] w-20 shrink-0">{t.date}</span>
              <span className="flex-1 truncate text-[#1C1C1C]">{t.origin} → {t.destination}{t.purpose ? <span className="text-[#595D5A]"> · {t.purpose}</span> : null}</span>
              <span className="text-xs text-[#595D5A] shrink-0">{t.total_miles} mi</span>
              <span className="font-semibold text-[#1C1C1C] shrink-0">{money(t.deduction)}</span>
              <button onClick={() => del(t.trip_id)} className="p-1 text-[#595D5A] hover:text-[#991B1B]" data-testid={`trip-delete-${t.trip_id}`}><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
          {trips.length > 8 && <li className="text-xs text-[#595D5A] px-1">+ {trips.length - 8} more in your CSV export</li>}
        </ul>
      )}
    </div>
  );
};

export default MileageTracker;
